import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"

import type { Tower } from "types/towers.types"

import { checkTowersByIdLoaded, getTowersById } from "helpers/utils/towers"
import { useLazyTowerQuery } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"

interface useSelectedTowersProps {
  towerIds: number[]
  skip?: boolean
}

const useSelectedTowers = ({ towerIds, skip = false }: useSelectedTowersProps) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [towers, setTowers] = useState<Tower[]>([])
  const [towersLoading, setTowersLoading] = useState(false)
  const [fetchTower] = useLazyTowerQuery()

  useEffect(() => {
    if (!orgId || skip) return

    if (towerIds.length) {
      setTowersLoading(true)
      Promise.all(
        towerIds.map(
          async (id) =>
            await fetchTower({ params: { org_id: Number(orgId) }, id }, true).unwrap(),
        ),
      )
        .then((res) => {
          setTowers(res)
          setTowersLoading(false)
        })
        .catch(() => {
          setTowersLoading(false)
        })
    }
  }, [fetchTower, orgId, towerIds, skip])

  const towersById = useMemo(() => getTowersById(towers), [towers])

  const towersByIdLoaded: boolean = useMemo(
    () => checkTowersByIdLoaded(towerIds, towersById),
    [towerIds, towersById],
  )

  return {
    towers,
    towersById,
    towersLoading,
    towersByIdLoaded,
  }
}

export default useSelectedTowers
